import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import CollapsibleCard from "../attendance-system/CollapsibleCard";
import SectionDivider from "../../components/SectionDivider";
import TextDiv from "../../components/TextDiv";
import RenderMatchStatus from "../../components/RenderMatchStatus";

// assets
import saveIcon from "../../assets/save_icon.png";
import infoBlue from "../../assets/info-blue.svg";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import CustomButton from "../../components/CustomButton";
import analyzeIcon from "../../assets/analyzeIcon.png";
import { DoneIcon, FailedIcon, WarningIcon } from "../../assets/registtyIcons/icons";
import { formatDate } from "../../utils/toDate";
import { useLocation } from "react-router-dom";
import { formatString } from "../../utils/formatString";
import SectionDividerWithNoLine from "../../components/SectionDividerWithNoLine";
import { extractNumericValue } from "../../utils/extractNumericValues";
import CustomInput from "../../components/CustomInput";
import {
  analyzeInvoiceForBank,
  createAnalyzedApplication,
  getAnalyzedInvoice,
  reportAnalysisBug,
} from "../../services/bank/banksAnalyzeService";
import { useEffect, useRef, useState } from "react";
import ErrorMessage from "../../components/ErrorMessage";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import StatusBadge from "../../components/StatusBadge";
import { hasCurrencySymbol } from "../../utils/currencySymbolChecker";
import RenderMatchPercentage from "../../components/RenderMatchPercentage";
import { useContext } from "react";
import { UserAuth } from "../../utils/AuthContext";
import {
  Radio,
  RadioGroup,
  RadioGroupItem,
  RadioLabel,
} from "../../components/Radio";
import axiosClient from "../../services/axiosClient";
import { buildInvoiceDocUrl, fetchFile } from "../../utils/files";
import LoaderWithProgress from "../../components/LoaderWithProgress";
import Tooltip from "../../components/Tooltip";
import HsCodeViewer from "../../components/hsCodeViewer";
import TextComparisonDisplay from "../../components/TextComparisonDisplay";
import Modal from "../../components/Modal";
import Textarea from "../../components/Textarea";
import useLanguage from "../../utils/useLanguage";
import CustomCheckbox from "../../components/CustomCheckbox";
import { animateTrailEffect } from "../../utils/trailAnimateEffect";
import CommonRejectionsReasons from "../../components/CommonRejectionsReasons";

const BanksAnalyzeInvoice = () => {
  // Hooks
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invoiceId = queryParams.get("invoiceId");
  const { userData } = useContext(UserAuth);

  const notesRef = useRef(null);

  const [analyzedData, setAnalyzedData] = useState(
    location.state?.data.analyzedData || null,
  );

  const [invoiceData, setInvoiceData] = useState(location.state?.data);
  const [commonRejections, setCommonRejections] = useState("");
  const [rejectionReason, setRejectionReason] = useState(
    invoiceData?.rejectionReason,
  );
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [manualNotes, setManualNotes] = useState("");
  const [autoGeneratedReasons, setAutoGeneratedReasons] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    completionState: "",
    subcompanySelection: "",
    supplierSelection: "",
  });
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [currentUserStatus, setCurrentUserStatus] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFetchingInvoice, setIsFetchingInvoice] = useState(false);
  const [fetchingInvoiceError, setFetchingInvoiceError] = useState();
  const [analyzeError, setAnalyzeError] = useState("");
  const [reAnalyze, setReAnalyze] = useState("");
  const [loadingHsCode, setLoadingHsCode] = useState(false);
  const [invoiceHsCodes, setInvoiceHsCodes] = useState([]);
  const [itemHsCodes, setItemHsCodes] = useState({});
  const [rejectedFields, setRejectedFields] = useState([]);

  // Bug Report State
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [bugReportDescription, setBugReportDescription] = useState("");
  const [isSubmittingBug, setIsSubmittingBug] = useState(false);
  const [bugReportSuccess, setBugReportSuccess] = useState(false);
  const [bugReportError, setBugReportError] = useState("");

  const handleReanalyzeInvoice = async () => {
    try {
      setIsAnalyzing(true);
      setAnalyzeError("");
      const docsResponse = await axiosClient.get(
        `/multiContainerInvoiceWithoutC/invoiceMainFiles/${invoiceId}`,
      );

      const invoiceDocs = docsResponse.data?.data?.documents;
      const invoiceFile = invoiceDocs.find((doc) => doc.type === "invoice");
      const bankAppFile = invoiceDocs.find(
        (doc) => doc.type === "BaghdadTransactionRequest",
      );
      const certFile = invoiceDocs.find(
        (doc) => doc.type === "supplierCertificate",
      );
      const assocFile = invoiceDocs.find(
        (doc) => doc.type === "articleOfAssociation",
      );
      let invoiceBlob;
      if (invoiceFile) {
        invoiceBlob = await fetchFile(buildInvoiceDocUrl(invoiceFile), "pdf");
      }
      let bankAppBlob;
      if (bankAppFile) {
        bankAppBlob = await fetchFile(buildInvoiceDocUrl(bankAppFile), "pdf");
      }
      let certBlob;
      if (certFile) {
        certBlob = await fetchFile(buildInvoiceDocUrl(certFile), "pdf");
      }
      let assocBlob;
      if (assocFile) {
        assocBlob = await fetchFile(buildInvoiceDocUrl(assocFile), "pdf");
      }
      const formData = new FormData();
      formData.append("invoice", invoiceBlob);
      formData.append("bankForm", bankAppBlob);
      formData.append("supplierCertificate", certBlob);
      formData.append("articlesOfAssociation", assocBlob);
      const response = await analyzeInvoiceForBank(formData);
      setAnalyzedData(response.data);
      setReAnalyze(true);
    } catch (error) {
      console.log("error analyzing invoice", error);

      if (error.response?.status === 503) {
        setAnalyzeError("errorAnalyzingInvoiceTooManyRequests");
        return;
      }
      setAnalyzeError("errorAnalyzingInvoice");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSave = () => {
    handleSaveAnalyzedApplication();
  };

  const handleFetchAnalyzedApplication = async () => {
    try {
      setFetchingInvoiceError("");
      setIsFetchingInvoice(true);
      const response = await getAnalyzedInvoice(invoiceId);
      window.history.replaceState({}, document.title);
      setInvoiceData(response.data);
      setAnalyzedData(response.data.applicationData);
      setRejectionReason(response.data.rejectionReason);
    } catch (error) {
      console.log("error analyzing invoice", error);
      setFetchingInvoiceError("errorFetchingAnalyzedInvoice");
    } finally {
      setIsFetchingInvoice(false);
    }
  };

  const handleSaveAnalyzedApplication = async () => {
    const userId = localStorage.getItem("userId");
    const isCreator = invoiceData.creatorId === userId;
    const isAuditor = invoiceData.auditorId === userId;

    const newErrors = { completionState: "" };

    if (currentUserStatus === null) {
      newErrors.completionState = "PleaseSelectCompletionState";
    }

    setValidationErrors(newErrors);

    if (newErrors.completionState || newErrors.rejectionReason) {
      return;
    }

    const status = currentUserStatus;

    try {
      setIsLoadingSave(true);
      await createAnalyzedApplication({
        applicationData: analyzedData,
        invoiceNumber: invoiceData?.invoice?.invoiceNumber,
        date: invoiceData?.invoice?.invoiceDate,
        rejectionReason: rejectionReason,
        creatorId: isCreator ? userId : null,
        auditorId: isAuditor ? userId : null,
        senderId: invoiceData?.sender?.id,
        receiverId: invoiceData?.receiver?.id,
        invoiceId: invoiceId,
        status,
        rejectionReason,
      });

      setIsEditing(false);

      await handleFetchAnalyzedApplication();
    } catch (error) {
      setError("errorSavingAnalyzedApplication");
      console.error(error);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const getHsCodes = async (hscode, setCodesCallback) => {
    try {
      setLoadingHsCode(true);
      const response = await axiosClient.get(
        `/hsCodes/getSpecificCode?code=${hscode}`,
      );

      const result = [
        {
          code: response?.data?.parent?.code,
          description: response?.data?.parent?.description,
        },
        {
          code: response?.data?.child1?.code,
          description: response?.data?.child1?.description,
        },
        {
          code: response?.data?.child2?.code,
          description: response?.data?.child2?.description,
        },
        {
          code: response?.data?.child3?.code,
          description: response?.data?.child3?.description,
        },
      ];

      setCodesCallback(result);
    } catch (error) {
      console.log("error getting hs codes", error);
    } finally {
      setLoadingHsCode(false);
    }
  };

  const handleBugReportSubmit = async () => {
    try {
      if (!bugReportDescription.trim()) return;
      setIsSubmittingBug(true);
      setBugReportError("");

      const docsResponse = await axiosClient.get(
        `/multiContainerInvoiceWithoutC/invoiceMainFiles/${invoiceId}`
      );
      const invoiceDocs = docsResponse.data?.data?.documents;

      const getFileBlob = async (type) => {
        const doc = invoiceDocs.find(d => d.type === type);
        if (doc) return await fetchFile(buildInvoiceDocUrl(doc), "pdf");
        return null;
      };

      const invoiceBlob = await getFileBlob("invoice");
      const bankAppBlob = await getFileBlob("BaghdadTransactionRequest");
      const certBlob = await getFileBlob("supplierCertificate");
      const assocBlob = await getFileBlob("articleOfAssociation");

      const formData = new FormData();
      formData.append("description", bugReportDescription);
      formData.append("analysisData", JSON.stringify(analyzedData));
      formData.append("invoiceId", invoiceId);
      if (invoiceBlob) formData.append("invoice", invoiceBlob);
      if (bankAppBlob) formData.append("bankForm", bankAppBlob);
      if (certBlob) formData.append("supplierCertificate", certBlob);
      if (assocBlob) formData.append("articlesOfAssociation", assocBlob);

      await reportAnalysisBug(formData);

      setBugReportSuccess(true);
      setTimeout(() => {
        setShowBugReportModal(false);
        setBugReportSuccess(false);
        setBugReportDescription("");
      }, 2000);

    } catch (err) {
      console.error(err);
      setBugReportError("errorSubmittingBugReport");
    } finally {
      setIsSubmittingBug(false);
    }
  };

  const supplierInvoiceDetails = analyzedData?.invoice?.supplier || {};
  const supplierWebsiteDetails = analyzedData?.supplierWebsiteScraper || {};
  const supplierArticleOfAssociation =
    analyzedData?.articlesOfAssociation || {};

  const items = analyzedData?.invoice?.items;

  const supplierBankDetails = analyzedData?.bankForm?.supplier || {};
  const supplierCertificateDetails = analyzedData?.supplierCertificate || {};

  // SIDE EFFECTS
  useEffect(() => {
    const fetchData = async () => {
      if (!location.state?.data && invoiceId) {
        await handleFetchAnalyzedApplication();
        return;
      }
      setIsEditing(true);
    };

    fetchData();
  }, [invoiceId]);

  useEffect(() => {
    const isCurrentUserCreator = invoiceData?.creatorId === userData.userId;
    const isCurrentUserAuditor = invoiceData?.auditorId === userData.userId;

    if (isCurrentUserCreator) {
      setCurrentUserStatus(invoiceData?.creatorStatus);
    } else if (isCurrentUserAuditor) {
      setCurrentUserStatus(invoiceData?.auditorStatus);
    }
  }, [invoiceData]);

  useEffect(() => {
    if (rejectedFields.length > 0) {
      const formattedReasons =
        `${commonRejections === "" ? t("rejectedForTheFolloingReasons") : ""
        }\n` +
        rejectedFields
          .map((field) => {
            const itemMatch = field.match(
              /^totalAmountForItem(\d+)DoesntMatchOrUncorrect$/,
            );

            if (itemMatch) {
              const itemNumber = itemMatch[1];
              const translatedText = t(
                "totalAmountForItemDoesntMatchOrUncorrect",
              );
              return `• ${translatedText.replace(
                "{itemNumber}",
                itemNumber,
              )}\n`;
            }

            return `• ${t(field)} ${field === "supplierNameNatively" ||
              field === "supplierAddressNatively"
                ? ""
                : field?.startsWith("totalAmount")
                  ? t("doesntExistsOrUncorrect")
                  : field === "website"
                    ? `${t("forSupplier")} ${t("doesntExistsInTheInvoice")}`
                    : t("doesntExistOrDoesntMatchTheInvoice")
            }\n`;
          })
          .join("");

      setAutoGeneratedReasons(
        commonRejections + formattedReasons + "\n" + manualNotes,
      );
    } else {
      setAutoGeneratedReasons(commonRejections + "\n" + manualNotes);
    }
  }, [rejectedFields, selectedReasons, commonRejections, t]);

  useEffect(() => {
    // Combine all three sources: auto-generated, dropdown selections, and manual notes
    const combined = [autoGeneratedReasons, manualNotes]
      .filter(Boolean)
      .join("\n\n")
      .trim();

    setRejectionReason(combined);
  }, [autoGeneratedReasons, manualNotes]);

  const ApplicationStatusSection = () => {
    return (
      <div
        className={`o-column o-column--gap-size_300 ${validationErrors.completionState ? "shake-animation-outline" : ""
          }`}
      >
        <RadioGroup>
          <RadioGroupItem>
            <Radio
              id="option1"
              value="ACCEPTED"
              checked={currentUserStatus === "ACCEPTED"}
              onChange={(e) => {
                setValidationErrors({
                  completionState: false,
                });
                setCurrentUserStatus(e.target.value);
              }}
            />
            <RadioLabel htmlFor="option1">{t("completed")}</RadioLabel>
          </RadioGroupItem>

          <RadioGroupItem>
            <Radio
              id="option2"
              value="REJECTED"
              checked={currentUserStatus === "REJECTED"}
              onChange={(e) => {
                setValidationErrors({
                  completionState: false,
                });
                setCurrentUserStatus(e.target.value);
              }}
            />
            <RadioLabel htmlFor="option2">{t("incomplete")}</RadioLabel>
          </RadioGroupItem>
        </RadioGroup>
      </div>
    );
  };

  const ComparisonFieldWithReject = ({
    title,
    value,
    compareValue,
    fieldName,
    showMatchPercentage = true,
    textDivProps = {},
    matchPercentageProps = {},
  }) => {
    const handleCheckboxChange = (checked) => {
      if (checked) {
        setRejectedFields((prev) => [...prev, fieldName]);
      } else {
        setRejectedFields((prev) => prev.filter((f) => f !== fieldName));
      }
    };

    return (
      <div className="o-column o-column--gap-size_300">
        <div className="o-column o-column--gap-size_300" style={{ flex: 1 }}>
          <TextDiv
            title={title}
            value={value}
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-width--min-width_300"
            {...textDivProps}
          />

          {showMatchPercentage && compareValue && (
            <RenderMatchPercentage
              value1={value}
              value2={compareValue}
              matchText="matchWithInvoice"
              spanStyle="u-width--min-width_300"
              {...matchPercentageProps}
            />
          )}
        </div>

        <div className="o-column-row-switcher">
          <span className="u-width--min-width_300"></span>
          {isEditing && (
            <div className="o-row o-row--vertical-align_center">
              <CustomCheckbox
                label={t("reject")}
                checked={rejectedFields.includes(fieldName)}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  handleCheckboxChange(isChecked);

                  if (isChecked) {
                    const checkboxWrapper = e.target.closest(
                      ".o-row.o-row--vertical-align_center",
                    );
                    if (checkboxWrapper) {
                      animateTrailEffect(checkboxWrapper, notesRef);
                    }
                  }
                }}
              />
              <span className="t-bold_bold">{t("reject")}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const ComparisonFieldWithRejectStatus = ({
    title,
    value,
    compareValue,
    fieldName,
    showMatchStatus = true,
    textDivProps = {},
    matchStatusProps = {},
  }) => {
    const handleCheckboxChange = (checked) => {
      if (checked) {
        setRejectedFields((prev) => [...prev, fieldName]);
      } else {
        setRejectedFields((prev) => prev.filter((f) => f !== fieldName));
      }
    };

    return (
      <div className="o-column">
        <div className="o-column o-column--gap-size_300" style={{ flex: 1 }}>
          {showMatchStatus && (
            <RenderMatchStatus
              spanStyle="u-width--min-width_300"
              {...matchStatusProps}
            />
          )}
        </div>

        {isEditing && (
          <div className="o-column-row-switcher">
            <span className="u-width--min-width_300"></span>
            <div>
              <CustomCheckbox
                label={t("reject")}
                checked={rejectedFields.includes(fieldName)}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  handleCheckboxChange(isChecked);

                  if (isChecked) {
                    const checkboxWrapper = e.target.closest(
                      ".o-column-row-switcher",
                    );
                    if (checkboxWrapper) {
                      animateTrailEffect(checkboxWrapper, notesRef);
                    }
                  }
                }}
              />
              <span className="t-bold_bold">{t("reject")}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isFetchingInvoice) {
    return (
      <PageWrapper withBackButton title={t("analyzeInvoices")}>
        <Loader />
      </PageWrapper>
    );
  }

  if (fetchingInvoiceError) {
    return (
      <PageWrapper withBackButton title={t("analyzeInvoices")}>
        <ErrorBox>{t(fetchingInvoiceError)}</ErrorBox>
      </PageWrapper>
    );
  }

  const shouldShowEditSaveReanalyzeButton =
    invoiceData?.creatorId === userData.userId ||
    invoiceData?.auditorId === userData.userId;

  return (
    <PageWrapper withBackButton title={t("analyzeInvoices")}>
      <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_400">
        <span>
          {userData?.userType === "organization"
            ? `${t("organization")}:`
            : `${t("user")}:`}
        </span>
        <span>{`${localStorage.getItem("username")}`}</span>
      </div>

      <div className="o-row o-row--horizontal-align_end u-margin--top_200">
        <CustomButton
          title={t("reportIssue") || "Report Issue"}
          icon={"bugReport"} // Using WarningIcon as flag
          onClick={() => setShowBugReportModal(true)}
          variant="blank"
          iconSize={24}
        />
      </div>

      {error && <ErrorBox>{t(error)}</ErrorBox>}
      {analyzeError && <ErrorBox>{t(analyzeError)}</ErrorBox>}
      {bugReportError && <ErrorBox>{t(bugReportError) || "Error submitting report"}</ErrorBox>}

      {showBugReportModal && (
        <Modal
          open={showBugReportModal}
          onClose={() => setShowBugReportModal(false)}
          title={t("reportAnalysisIssue") || "Report Analysis Issue"}
          footer={
            <div className="o-row o-row--horizontal-align_end o-row--gap-size_200">
              <CustomButton
                title={t("cancel")}
                variant="blank"
                icon={closeIcon}
                iconSize={14}
                onClick={() => setShowBugReportModal(false)}
                disabled={isSubmittingBug}
              />
              <CustomButton
                title={bugReportSuccess ? t("sent") || "Sent" : t("submit")}
                onClick={handleBugReportSubmit}
                loading={isSubmittingBug}
                disabled={isSubmittingBug || !bugReportDescription.trim()}
                icon={bugReportSuccess ? DoneIcon : saveIcon}
              />
            </div>
          }
        >
          <div className="o-column o-column--gap-size_200">
            <p>{t("describeIssue") || "Please describe the issue with the analysis:"}</p>
            <Textarea
              value={bugReportDescription}
              onChange={(e) => setBugReportDescription(e.target.value)}
              placeholder={t("issueDescriptionPlaceholder") || "E.g. The supplier name is incorrect..."}
              rows={5}
            />
          </div>
        </Modal>
      )}

      {invoiceId && (
        <>
          <SectionDivider title={t("creatorAndAuditor")} />
          <div className="o-column o-column--gap-size_600 u-margin_0">
            <div className="o-column o-column--gap-size_200">
              <TextDiv
                title="creator"
                value={invoiceData?.creator?.username}
                divClasses="o-row"
                pClasses="t-bold_semi-bold u-width--min-width_100"
              />

              <div className="o-row o-row--vertical-align_center">
                <label className="u-width--min-width_100">
                  {t("completionStatus")}:
                </label>
                {invoiceData?.creatorStatus === "NOT_STARTED" ||
                  invoiceData?.creatorStatus == undefined ? (
                  <StatusBadge color={"gray"} title={"notStarted"} />
                ) : invoiceData?.creatorStatus === "PENDING" ? (
                  <StatusBadge color={"blue"} title={"inProgress"} />
                ) : invoiceData?.creatorStatus === "REJECTED" ? (
                  <StatusBadge color={"red"} title={"declined"} />
                ) : invoiceData?.creatorStatus === "ACCEPTED" ? (
                  <StatusBadge color={"green"} title={"approved"} />
                ) : null}
              </div>
            </div>

            <div className="o-column o-column--gap-size_200">
              <TextDiv
                title="auditor"
                value={invoiceData?.auditor?.username}
                divClasses="o-row"
                pClasses="t-bold_semi-bold u-width--min-width_100"
              />

              <div className="o-row o-row--vertical-align_center">
                <label className="u-width--min-width_100">
                  {t("completionStatus")}:
                </label>
                {invoiceData?.auditorStatus === "NOT_STARTED" ||
                  invoiceData?.auditorStatus == undefined ? (
                  <StatusBadge color={"gray"} title={"notStarted"} />
                ) : invoiceData?.auditorStatus === "PENDING" ? (
                  <StatusBadge color={"blue"} title={"inProgress"} />
                ) : invoiceData?.auditorStatus === "REJECTED" ? (
                  <StatusBadge color={"red"} title={"declined"} />
                ) : invoiceData?.auditorStatus === "ACCEPTED" ? (
                  <StatusBadge color={"green"} title={"approved"} />
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="u-margin--top_500">
        <SectionDivider title={t("senderAndReceiver")} />
        <div className="o-column o-column--gap-size_300">
          <TextDiv
            title="sender"
            value={
              currentDirection === "rtl"
                ? invoiceData?.sender?.nameInAr
                : invoiceData?.sender?.name
            }
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-width--min-width_100"
          />
          <TextDiv
            title="receiver"
            value={invoiceData?.receiver?.name}
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-width--min-width_100"
          />
        </div>
      </div>
      <div className="o-column o-column--gap-size_400">
        <div>
          <SectionDivider title={"supplier"} />
          <CollapsibleCard
            title={`${t("supplierDetails")} (${t("receiver")})`}
            collapsed={true}
            body={
              <>
                {/* Supplier name section */}
                <section>
                  <SectionDivider title={t("supplierName")} noMargin />
                  <div className="o-column o-column--gap-size_500 u-margin--top_400">
                    {/* invoice */}
                    <TextDiv
                      title="supplierNameInInvoice"
                      value={supplierInvoiceDetails.name}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    {/* bank application */}
                    <ComparisonFieldWithReject
                      title="supplierNameInBankApplication"
                      value={supplierBankDetails.name}
                      compareValue={supplierInvoiceDetails.name}
                      fieldName="supplierNameInBankApplication"
                    />

                    {/* website */}
                    <ComparisonFieldWithReject
                      title="supplierNameInWebsite"
                      value={supplierWebsiteDetails.companyName}
                      compareValue={supplierInvoiceDetails.name}
                      fieldName="supplierNameInWebsite"
                    />

                    {/* certificate */}
                    <ComparisonFieldWithReject
                      title="supplierNameInCertificate"
                      value={supplierCertificateDetails?.companyName}
                      compareValue={supplierInvoiceDetails.name}
                      fieldName="supplierNameInCertificate"
                    />

                    {/* article of association */}
                    {invoiceData?.toHongKong && (
                      <ComparisonFieldWithReject
                        title="supplierNameInArticleOfAssociation"
                        value={supplierArticleOfAssociation?.companyName}
                        compareValue={supplierInvoiceDetails.name}
                        fieldName="supplierNameInArticleOfAssociation"
                      />
                    )}
                  </div>
                </section>

                {/* Address section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("address")} />
                  <div className="o-column o-column--gap-size_500">
                    {/* invoice */}
                    <TextDiv
                      title="addressInInvoice"
                      value={supplierInvoiceDetails.address}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr t-text-align_center"
                    />
                    {/* bank application */}
                    <ComparisonFieldWithReject
                      title="addressInBankApplication"
                      value={supplierBankDetails.address}
                      compareValue={supplierInvoiceDetails.address}
                      fieldName="addressInBankApplication"
                      textDivProps={{
                        spanClasses: "dir-ltr t-text-align_center",
                      }}
                    />
                    {/* website */}
                    <ComparisonFieldWithReject
                      title="addressInWebsite"
                      value={supplierWebsiteDetails.companyAddress}
                      compareValue={supplierInvoiceDetails.address}
                      fieldName="addressInWebsite"
                      textDivProps={{
                        spanClasses: "dir-ltr t-text-align_center",
                      }}
                    />
                    {/* certificate */}
                    <ComparisonFieldWithReject
                      title="addressInCertificate"
                      value={supplierCertificateDetails.address}
                      compareValue={supplierInvoiceDetails.address}
                      fieldName="addressInCertificate"
                      textDivProps={{
                        spanClasses: "dir-ltr t-text-align_center",
                      }}
                    />
                    {/* article of association */}
                    {invoiceData?.toHongKong && (
                      <ComparisonFieldWithReject
                        title="supplierAddressInArticleOfAssociation"
                        value={supplierArticleOfAssociation?.address}
                        compareValue={supplierInvoiceDetails.address}
                        fieldName="supplierAddressInArticleOfAssociation"
                        textDivProps={{
                          spanClasses: "dir-ltr t-text-align_center",
                        }}
                      />
                    )}
                  </div>
                </section>

                {/* Phone number section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("phoneNumber")} />
                  <div className="o-column o-column--gap-size_500">
                    {/* invoice */}
                    <TextDiv
                      title="phoneNumberInInvoice"
                      value={supplierInvoiceDetails.phoneNumber}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr"
                    />

                    {/* bank application */}
                    <ComparisonFieldWithReject
                      title="phoneNumberInBankApplication"
                      value={supplierBankDetails.phoneNumber}
                      compareValue={supplierInvoiceDetails.phoneNumber}
                      fieldName="phoneNumberInBankApplication"
                      textDivProps={{
                        spanClasses: "dir-ltr",
                      }}
                      matchPercentageProps={{
                        spanClasses: "dir-ltr",
                      }}
                    />

                    {/* website */}
                    <ComparisonFieldWithReject
                      title="phoneNumberInWebsite"
                      value={supplierWebsiteDetails.phoneNumber}
                      compareValue={supplierInvoiceDetails.phoneNumber}
                      fieldName="phoneNumberInWebsite"
                      textDivProps={{
                        spanClasses: "dir-ltr",
                      }}
                    />
                  </div>
                </section>

                {/* Email section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("email")} />
                  <div className="o-column o-column--gap-size_500">
                    {/* invoice */}
                    <TextDiv
                      title="emailInInvoice"
                      value={supplierInvoiceDetails.email}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr"
                    />

                    {/* bank application */}
                    <TextDiv
                      title="emailInBankApplication"
                      value={supplierBankDetails.email}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr"
                    />
                    <ComparisonFieldWithRejectStatus
                      fieldName="emailInBankApplication"
                      value={supplierBankDetails.email}
                      compareValue={supplierInvoiceDetails.email}
                      matchStatusProps={{
                        value1: supplierBankDetails.email,
                        value2: supplierInvoiceDetails.email,
                        matchText: "matchWithInvoice",
                        compareAsString: true,
                      }}
                    />

                    {/* website */}
                    <TextDiv
                      title="emailInWebsite"
                      value={supplierWebsiteDetails.email}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr"
                    />
                    <ComparisonFieldWithRejectStatus
                      fieldName="emailInWebsite"
                      value={supplierWebsiteDetails.email}
                      compareValue={supplierInvoiceDetails.email}
                      matchStatusProps={{
                        value1: supplierWebsiteDetails.email,
                        value2: supplierInvoiceDetails.email,
                        matchText: "matchWithInvoice",
                        compareAsString: true,
                      }}
                    />
                  </div>
                </section>

                {/* Website section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("website")} />
                  <div className="o-column o-column--gap-size_500">
                    {/* invoice */}
                    <div className="o-column o-column--gap-size_200">
                      <section className="o-column-row-switcher ">
                        <span className="u-width--min-width_300 t-bold_semi-bold">
                          {t("websiteInInvoice")}:{" "}
                        </span>
                        {supplierInvoiceDetails?.website ? (

                          <div className="o-row o-row--vertical-align_center">
                            <a
                              href={
                                supplierInvoiceDetails.website &&
                                supplierInvoiceDetails.website?.startsWith(
                                  "http",
                                )
                                  ? supplierInvoiceDetails.website
                                  : `http://${supplierInvoiceDetails.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link-color t-bold_bold"
                            >
                              {supplierInvoiceDetails.website}
                            </a>
                            <Tooltip
                              content={
                                <div className="o-column o-column--gap-size_200">
                                  <p className="t-bold_semi-bold">{t("websiteImageFromInvoice")}:</p>

                                  <img
                                    src={supplierInvoiceDetails?.websiteImage}
                                  />
                                </div>
                              }
                              maxWidth={500}
                            >
                              <img src={infoBlue} className="tooltip-img" />
                            </Tooltip>
                          </div>
                        ) : (
                          <span className="not-working-website">
                            {t(supplierInvoiceDetails.website?.urlStatus)}
                          </span>
                        )}
                      </section>

                      <TextDiv
                        title="websiteCreationDate"
                        value={formatDate(
                          supplierWebsiteDetails?.foundationDate,
                        )}
                        divClasses="o-column-row-switcher"
                        pClasses="u-width--min-width_300"
                      />

                      <section className="o-row o-row--vertical-align_center o-row--horizontal-align_space-between">
                        <div className="o-column-row-switcher">
                          <span className="u-width--min-width_300">
                            {t("websiteStatus")}:{" "}
                          </span>

                          {supplierWebsiteDetails.urlStatus !==
                            "noURLProvided" ? (
                            <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                              {supplierWebsiteDetails.urlStatus ===
                                "working" ? (
                                <DoneIcon width={20} height={20} />
                              ) : supplierWebsiteDetails.urlStatus ===
                                "notWorking" ? (
                                <FailedIcon width={20} height={20} />
                              ) : supplierWebsiteDetails.urlStatus !==
                                "notExist" ? (
                                <WarningIcon width={20} height={20} />
                              ) : (
                                <FailedIcon width={20} height={20} />
                              )}

                              <span
                                className={
                                  supplierWebsiteDetails.urlStatus === "working"
                                    ? "working-website"
                                    : supplierWebsiteDetails.urlStatus ===
                                        "notWorking"
                                      ? "not-working-website"
                                      : supplierWebsiteDetails.urlStatus ===
                                          "notExist"
                                        ? "cannot-reach-website"
                                        : "cannot-reach-website"
                                }
                              >
                                {t(
                                  supplierWebsiteDetails?.urlStatus?.toLowerCase() ||
                                    "notExist",
                                )}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </section>

                      {isEditing && (
                        <div className="o-column-row-switcher">
                          <span className="u-width--min-width_300"></span>
                          <div>
                            <CustomCheckbox
                              label={t("reject")}
                              checked={rejectedFields.includes("website")}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (checked) {
                                  setRejectedFields((prev) => [
                                    ...prev,
                                    "website",
                                  ]);

                                  const checkboxWrapper = e.target.closest(
                                    ".o-column-row-switcher",
                                  );
                                  if (checkboxWrapper) {
                                    animateTrailEffect(
                                      checkboxWrapper,
                                      notesRef,
                                    );
                                  }
                                } else {
                                  setRejectedFields((prev) =>
                                    prev.filter((f) => f !== "website"),
                                  );
                                }
                              }}
                            />
                            <span className="t-bold_bold">{t("reject")}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* certicate and association comparison */}

                {invoiceData?.toHongKong && (
                  <>
                    <section>
                      <SectionDivider
                        title={t("certificateAndAssociationComparison")}
                      />

                      <div className="o-column o-column--gap-size_400">
                        {
                          <TextComparisonDisplay
                            title1="supplierNameInCertificate"
                            title2="supplierNameInArticleOfAssociation"
                            value1={
                              supplierCertificateDetails?.companyNameNative
                            }
                            value2={
                              supplierArticleOfAssociation?.companyNameNative
                            }
                            matchText="matchState"
                            spanStyle="u-width--min-width_300"
                            titleClasses="t-bold_semi-bold u-width--min-width_300"
                            divClasses="o-column o-column--gap-size_400 u-margin--top_400"
                          />
                        }
                        {isEditing && (
                          <div className="o-column-row-switcher u-margin--bottom_600">
                            <span className="u-width--min-width_300"></span>
                            <div>
                              <CustomCheckbox
                                label={t("reject")}
                                checked={rejectedFields.includes(
                                  "supplierNameNatively",
                                )}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  if (checked) {
                                    setRejectedFields((prev) => [
                                      ...prev,
                                      "supplierNameNatively",
                                    ]);

                                    const checkboxWrapper = e.target.closest(
                                      ".o-column-row-switcher",
                                    );
                                    if (checkboxWrapper) {
                                      animateTrailEffect(
                                        checkboxWrapper,
                                        notesRef,
                                      );
                                    }
                                  } else {
                                    setRejectedFields((prev) =>
                                      prev.filter(
                                        (f) => f !== "supplierNameNatively",
                                      ),
                                    );
                                  }
                                }}
                              />
                              <span className="t-bold_bold">{t("reject")}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {
                        <TextComparisonDisplay
                          title1="addressInCertificate"
                          title2="supplierAddressInArticleOfAssociation"
                          value1={
                            supplierCertificateDetails?.addressNative || ""
                          }
                          value2={
                            supplierArticleOfAssociation?.addressNative || ""
                          }
                          matchText="matchState"
                          spanStyle="u-width--min-width_300"
                          titleClasses="t-bold_semi-bold u-width--min-width_300"
                          divClasses="o-column o-column--gap-size_400 u-margin--top_400"
                        />
                      }

                      {isEditing && (
                        <div className="o-column-row-switcher">
                          <span className="u-width--min-width_300"></span>
                          <div>
                            <CustomCheckbox
                              label={t("reject")}
                              checked={rejectedFields.includes(
                                "supplierAddressNatively",
                              )}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (checked) {
                                  setRejectedFields((prev) => [
                                    ...prev,
                                    "supplierAddressNatively",
                                  ]);

                                  const checkboxWrapper = e.target.closest(
                                    ".o-column-row-switcher",
                                  );
                                  if (checkboxWrapper) {
                                    animateTrailEffect(
                                      checkboxWrapper,
                                      notesRef,
                                    );
                                  }
                                } else {
                                  setRejectedFields((prev) =>
                                    prev.filter(
                                      (f) => f !== "supplierAddressNatively",
                                    ),
                                  );
                                }
                              }}
                            />
                            <span className="t-bold_bold">{t("reject")}</span>
                          </div>
                        </div>
                      )}
                    </section>

                    {/* supplier shareholders */}
                    <section>
                      <SectionDivider title={t("shareHolders")} />
                      {supplierArticleOfAssociation?.shareHolders?.map(
                        (shareHolder, index) => (
                          <div className="u-margin--bottom_600" key={index}>
                            <SectionDividerWithNoLine
                              title={`${t("shareHolder")} ${index + 1}`}
                              spanClasses="t-bold_extra-bold"
                              noMargin
                            />

                            <div className="o-column o-column--gap-size_300 u-margin--top_400">
                              <TextDiv
                                title={"name"}
                                value={shareHolder.name}
                                divClasses="o-column-row-switcher"
                                pClasses="t-bold_semi-bold u-width--min-width_300"
                              />
                              <TextDiv
                                title="address"
                                value={shareHolder.address}
                                divClasses="o-column-row-switcher"
                                pClasses="t-bold_semi-bold u-width--min-width_300"
                                spanClasses="dir-ltr t-text-align_center"
                              />
                              <TextDiv
                                title="idNumber"
                                value={shareHolder.residentIdNumber}
                                divClasses="o-column-row-switcher"
                                pClasses="t-bold_semi-bold u-width--min-width_300"
                                spanClasses="dir-ltr"
                              />
                              <TextDiv
                                title="contributionAmount"
                                value={`${shareHolder.contributionAmount} ${shareHolder.contributionCurrency}`}
                                divClasses="o-column-row-switcher"
                                pClasses="t-bold_semi-bold u-width--min-width_300"
                                spanClasses="dir-ltr"
                              />
                              <TextDiv
                                title="contributionPercentage"
                                value={`${shareHolder.contributionPercentage} %`}
                                divClasses="o-column-row-switcher"
                                pClasses="t-bold_semi-bold u-width--min-width_300"
                                spanClasses="dir-ltr t-bold_bold positive-value"
                              />
                              <TextDiv
                                title="contributionPayDate"
                                value={
                                  shareHolder?.paidDate?.startsWith("before")
                                    ? `${t("before")} ${formatDate(
                                        shareHolder.paidDate,
                                      )}`
                                    : formatDate(shareHolder?.paidDate)
                                }
                                divClasses="o-column-row-switcher"
                                pClasses="t-bold_semi-bold u-width--min-width_300"
                              />
                            </div>
                          </div>
                        ),
                      )}
                    </section>
                  </>
                )}
              </>
            }
          />
        </div>

        <div>
          <SectionDivider title={"invoice"} />
          <CollapsibleCard
            title={t("invoiceDetails")}
            collapsed={true}
            body={
              <>
                {/* invoice details*/}
                <section>
                  <SectionDivider title={t("invoice")} noMargin />
                  <div className="o-column o-column--gap-size_400 u-margin--top_400">
                    <TextDiv
                      title="invoiceNumber"
                      value={analyzedData?.invoice?.invoiceNumber || ""}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <TextDiv
                      title="invoiceDate"
                      value={analyzedData?.invoice?.invoiceDate || ""}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    {/* country of origin */}
                    <TextDiv
                      title="countryOfOrigin"
                      value={
                        analyzedData?.invoice?.originOfProduct || (
                          <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                            <FailedIcon width={20} height={20} />

                            <span className={"not-working-website"}>
                              {t("notFound")}
                            </span>
                          </div>
                        )
                      }
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    {isEditing && (
                      <div className="o-column-row-switcher">
                        <span className="u-width--min-width_300"></span>
                        <div>
                          <CustomCheckbox
                            label={t("reject")}
                            checked={rejectedFields.includes("countryOfOrigin")}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (checked) {
                                setRejectedFields((prev) => [
                                  ...prev,
                                  "countryOfOrigin",
                                ]);

                                const checkboxWrapper = e.target.closest(
                                  ".o-column-row-switcher",
                                );
                                if (checkboxWrapper) {
                                  animateTrailEffect(checkboxWrapper, notesRef);
                                }
                              } else {
                                setRejectedFields((prev) =>
                                  prev.filter((f) => f !== "countryOfOrigin"),
                                );
                              }
                            }}
                          />
                          <span className="t-bold_bold">{t("reject")}</span>
                        </div>
                      </div>
                    )}

                    {/* Terms */}
                    {
                      <TextDiv
                        title="shippingTerms"
                        value={
                          analyzedData?.invoice?.terms?.[0]?.shippingTerms || (
                            <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                              <FailedIcon width={20} height={20} />

                              <span className={"not-working-website"}>
                                {t("notFound")}
                              </span>
                            </div>
                          )
                        }
                        divClasses="o-column-row-switcher"
                        pClasses="t-bold_semi-bold u-width--min-width_300"
                      />
                    }

                    {isEditing && (
                      <div className="o-column-row-switcher">
                        <span className="u-width--min-width_300"></span>
                        <div>
                          <CustomCheckbox
                            label={t("reject")}
                            checked={rejectedFields.includes("shippingTerms")}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (checked) {
                                setRejectedFields((prev) => [
                                  ...prev,
                                  "shippingTerms",
                                ]);

                                const checkboxWrapper = e.target.closest(
                                  ".o-column-row-switcher",
                                );
                                if (checkboxWrapper) {
                                  animateTrailEffect(checkboxWrapper, notesRef);
                                }
                              } else {
                                setRejectedFields((prev) =>
                                  prev.filter((f) => f !== "shippingTerms"),
                                );
                              }
                            }}
                          />
                          <span className="t-bold_bold">{t("reject")}</span>
                        </div>
                      </div>
                    )}

                    {
                      <TextDiv
                        title="paymentTerms"
                        value={
                          analyzedData?.invoice?.terms?.[0]?.paymentTerms || (
                            <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                              <FailedIcon width={20} height={20} />

                              <span className={"not-working-website"}>
                                {t("notFound")}
                              </span>
                            </div>
                          )
                        }
                        divClasses="o-column-row-switcher"
                        pClasses="t-bold_semi-bold u-width--min-width_300"
                      />
                    }

                    {isEditing && (
                      <div className="o-column-row-switcher">
                        <span className="u-width--min-width_300"></span>
                        <div>
                          <CustomCheckbox
                            label={t("reject")}
                            checked={rejectedFields.includes("paymentTerms")}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (checked) {
                                setRejectedFields((prev) => [
                                  ...prev,
                                  "paymentTerms",
                                ]);

                                const checkboxWrapper = e.target.closest(
                                  ".o-column-row-switcher",
                                );
                                if (checkboxWrapper) {
                                  animateTrailEffect(checkboxWrapper, notesRef);
                                }
                              } else {
                                setRejectedFields((prev) =>
                                  prev.filter((f) => f !== "paymentTerms"),
                                );
                              }
                            }}
                          />
                          <span className="t-bold_bold">{t("reject")}</span>
                        </div>
                      </div>
                    )}

                    <TextDiv
                      title="totalAmountFromInvoice"
                      value={
                        hasCurrencySymbol(analyzedData?.invoice?.totalAmount)
                          ? formatString(analyzedData?.invoice?.totalAmount)
                          : `${formatString(
                              analyzedData?.invoice?.totalAmount || "",
                            )} ${analyzedData?.invoice?.currency || ""}`
                      }
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <TextDiv
                      title="calculatedTotalPrice"
                      value={`${analyzedData?.invoice?.calculatedTotalPrice || ""
                        } ${analyzedData?.invoice?.currency || ""}`}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <ComparisonFieldWithRejectStatus
                      title={"invoiceTotalAmountMatch"}
                      value={`${formatString(
                        analyzedData?.invoice?.totalAmount,
                      )} vs ${analyzedData?.invoice?.calculatedTotalPrice}`}
                      fieldName="totalAmount"
                      matchStatusProps={{
                        value1: extractNumericValue(
                          analyzedData?.invoice?.totalAmount,
                        ),
                        value2: parseFloat(
                          analyzedData?.invoice?.calculatedTotalPrice,
                        ),
                        matchText: "itemTotalAmountMatch",
                      }}
                    />
                  </div>
                </section>

                {/* items */}
                <section className="u-margin--top_500">
                  <SectionDivider title={"items"} noMargin={true} />
                  {analyzedData?.invoice?.hscode && (
                    <div className="o-row u-margin--top_500">
                      <TextDiv
                        title={"hsCode"}
                        value={`${analyzedData?.invoice?.hscode}`}
                        divClasses="o-column-row-switcher"
                        pClasses="t-bold_semi-bold u-width--min-width_300"
                      />
                      <Tooltip
                        direction="ltr"
                        textAlign="left"
                        maxWidth="400px"
                        content={
                          loadingHsCode ? (
                            <Loader size={LOADER_SIZES.SMALL} />
                          ) : (
                            <HsCodeViewer hsCodes={invoiceHsCodes} />
                          )
                        }
                        onShow={() => {
                          if (
                            analyzedData?.invoice?.hscode &&
                            invoiceHsCodes.length === 0
                          ) {
                            getHsCodes(
                              analyzedData?.invoice?.hscode,
                              setInvoiceHsCodes,
                            );
                          }
                        }}
                      >
                        <img src={infoBlue} className="tooltip-img" />
                      </Tooltip>
                    </div>
                  )}
                  {items?.map((item, index) => {
                    return (
                      <div
                        className="o-column o-column--gap-size_300 u-margin--top_600"
                        key={index}
                      >
                        <SectionDividerWithNoLine
                          title={`${t("item")} ${index + 1}`}
                          noMargin={true}
                          spanClasses="t-bold_extra-bold"
                        />

                        <TextDiv
                          title={"name/no"}
                          value={item["name/no"]}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <TextDiv
                          title="description"
                          value={item.description}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />

                        <div className="o-row">
                          <TextDiv
                            title="hsCode"
                            value={
                              item.hscode || (
                                <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                                  <FailedIcon width={20} height={20} />
                                  <span className={"not-working-website"}>
                                    {t("notFound")}
                                  </span>
                                </div>
                              )
                            }
                            divClasses="o-column-row-switcher"
                            pClasses="t-bold_semi-bold u-width--min-width_300"
                          />
                          {item?.hscode && (
                            <Tooltip
                              direction="ltr"
                              textAlign="left"
                              maxWidth="400px"
                              content={
                                loadingHsCode ? (
                                  <Loader size={LOADER_SIZES.SMALL} />
                                ) : (
                                  <HsCodeViewer
                                    hsCodes={itemHsCodes[index] || []}
                                  />
                                )
                              }
                              onShow={() => {
                                if (item?.hscode && !itemHsCodes[index]) {
                                  getHsCodes(item?.hscode, (codes) => {
                                    setItemHsCodes((prev) => ({
                                      ...prev,
                                      [index]: codes,
                                    }));
                                  });
                                }
                              }}
                            >
                              <img src={infoBlue} className="tooltip-img" />
                            </Tooltip>
                          )}
                        </div>

                        <TextDiv
                          title="quantity"
                          value={`${item.quantity} ${item.measuringUnit || ""}`}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                          spanClasses="dir-ltr"
                        />
                        <TextDiv
                          title="unitPrice"
                          value={
                            hasCurrencySymbol(item?.unitPrice)
                              ? formatString(item.unitPrice)
                              : `${formatString(item.unitPrice)} ${analyzedData?.invoice?.currency
                              }`
                          }
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <TextDiv
                          title="totalAmountFromInvoice"
                          value={
                            hasCurrencySymbol(item.totalPrice)
                              ? formatString(item.totalPrice)
                              : `${formatString(item.totalPrice)} ${analyzedData?.invoice?.currency
                              }`
                          }
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />

                        <TextDiv
                          title="calculatedTotalPrice"
                          value={`${item.calculatedPrice} ${analyzedData?.invoice?.currency}`}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />

                        <ComparisonFieldWithRejectStatus
                          title={`${"item"}${index + 1}${"TotalAmountMatch"}`}
                          value={`${formatString(item.totalPrice)} vs ${item.calculatedPrice
                            }`}
                          fieldName={`totalAmountForItem${index + 1
                            }DoesntMatchOrUncorrect`}
                          matchStatusProps={{
                            value1: extractNumericValue(item.totalPrice),
                            value2: parseFloat(item.calculatedPrice),
                            matchText: "itemTotalAmountMatch",
                          }}
                        />
                      </div>
                    );
                  })}
                </section>
              </>
            }
          />
        </div>

        {isEditing && (
          <div className="u-margin--top_700">
            <CommonRejectionsReasons
              selectedReasons={selectedReasons}
              setSelectedReasons={setSelectedReasons}
              onRejectionReasonChange={setCommonRejections}
            // currentRejectionReason={rejectionReason}
            />
          </div>
        )}

        <section className="u-margin--top_200">
          {isEditing || !invoiceId ? (
            <div ref={notesRef}>
              <CustomInput
                type="textarea"
                title={`${t("notes")}:`}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          ) : (
            <div>
              <label className="t-bold_bold">{t("notes")}:</label>
              <p style={{ whiteSpace: "pre-line" }}>{rejectionReason}</p>
            </div>
          )}

          {isEditing ? (
            <div className="o-column o-column--gap-size_400 u-margin--top_400">
              <label className="t-bold_bold">{t("status")}:</label>
              <ApplicationStatusSection />
            </div>
          ) : null}
        </section>
        {isAnalyzing && (
          <div className="o-row o-row--horizontal-align_center">
            {<LoaderWithProgress loadingText={t("analyzingInvoice")} />}
          </div>
        )}

        {isLoadingSave && <Loader />}

        <section className="u-margin--top_700 o-column o-column--vertical-align_end">
          <div className="u-width--fit-content">
            {validationErrors.completionState && (
              <div className="o-row o-row--horizontal-align_end u-margin--top_200">
                <ErrorMessage>{t("selectCompletionStatus")}</ErrorMessage>
              </div>
            )}
            {error && (
              <div className="o-row o-row--horizontal-align_end u-margin--top_200">
                <ErrorMessage>{t(error)}</ErrorMessage>
              </div>
            )}
            {analyzeError && (
              <div className="o-row o-row--horizontal-align_end u-margin--top_200">
                <ErrorMessage>{t(analyzeError)}</ErrorMessage>
              </div>
            )}

            <div className="o-row o-row--gap-size_300 o-row--horizontal-align_end u-margin--top_300">
              {reAnalyze && (
                <>
                  <CustomButton
                    onClick={() => {
                      handleFetchAnalyzedApplication();
                      setReAnalyze(false);
                    }}
                    title={t("cancelReAnalyze")}
                    variant={"blank"}
                    icon={closeIcon}
                    iconSize={16}
                    disabled={isAnalyzing}
                  />
                  {shouldShowEditSaveReanalyzeButton && (
                    <CustomButton
                      onClick={handleSaveAnalyzedApplication}
                      title={t("saveReAnalyzeResults")}
                      icon={saveIcon}
                      iconSize={16}
                      disabled={isAnalyzing}
                    />
                  )}
                </>
              )}
              {invoiceId &&
                !isEditing &&
                !reAnalyze &&
                shouldShowEditSaveReanalyzeButton && (
                  <CustomButton
                    onClick={() => setIsEditing(true)}
                    title={t("edit")}
                    variant={"blank"}
                    icon={editIcon}
                    iconSize={16}
                    disabled={isAnalyzing}
                  />
                )}
              {isEditing && !reAnalyze && (
                <CustomButton
                  onClick={() => {
                    setIsEditing(false);
                    handleFetchAnalyzedApplication();
                  }}
                  title={t("cancel")}
                  variant={"blank"}
                  icon={closeIcon}
                  iconSize={15}
                  disabled={isAnalyzing}
                />
              )}

              {invoiceId &&
                !isEditing &&
                !reAnalyze &&
                shouldShowEditSaveReanalyzeButton ? (
                <CustomButton
                  onClick={handleReanalyzeInvoice}
                  title={t("reAnalyze")}
                  variant="blank"
                  icon={analyzeIcon}
                  iconSize={20}
                  disabled={isAnalyzing}
                />
              ) : null}

              {(!invoiceId || isEditing) && (
                <CustomButton
                  onClick={handleSave}
                  title={t("save")}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={isLoadingSave}
                />
              )}
            </div>
          </div>
        </section>
      </div>
    </PageWrapper>
  );
};

export default BanksAnalyzeInvoice;
